import React, { useState } from "react";
import { toast } from "react-toastify";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import { responseMessage } from "../../../services/platformService";
import MasterSection from "../../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { EmptyState, Field, FormDrawer, LoadState, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, inputStyle, todayYmd, useLoad } from "../../../components/ui";

// Dealership Setup tabs for vehicle sales: catalogue, order types, price lists, services offered,
// campaigns, checklists and the sales team.
const CODE = { key: "code", label: "Code", required: true, max: 24, hint: "Letters, digits, - and _" };
const NAME = { key: "name", label: "Name", required: true, max: 100 };
const m = (key) => ({ load: () => dmsService.listMaster(key), save: (id, data) => dmsService.saveMaster(key, id, data) });
const opt = (list) => list.map(([value, label]) => ({ label, value }));
const labelOf = (list, v) => (list.find(([x]) => x === v) || [])[1] || v || "—";
const BODY = [["SEDAN", "Sedan"], ["SUV", "SUV"], ["HATCHBACK", "Hatchback"], ["PICKUP", "Pick-up"], ["VAN", "Van"], ["TRUCK", "Truck"], ["COUPE", "Coupe"], ["BUS", "Bus"], ["OTHER", "Other"]];
const FUEL = [["PETROL", "Petrol"], ["DIESEL", "Diesel"], ["HYBRID", "Hybrid"], ["PHEV", "Plug-in hybrid"], ["ELECTRIC", "Electric"], ["LPG", "LPG"], ["OTHER", "Other"]];
const GEAR = [["AUTOMATIC", "Automatic"], ["MANUAL", "Manual"], ["CVT", "CVT"], ["DCT", "Dual clutch"], ["OTHER", "Other"]];
const STOCK = [["NEW", "New"], ["USED", "Used"], ["DEMO", "Demo"]];
const GROUPS = [["REGISTRATION", "Registration"], ["INSURANCE", "Insurance"], ["WARRANTY", "Warranty"], ["ACCESSORY", "Accessory"], ["PACKAGE", "Package"], ["SERVICE", "Service"], ["OTHER", "Other"]];
const LINE_TYPES = [["CHARGE_REVENUE", "Services income"], ["ACCESSORY_REVENUE", "Accessories income"], ["SERVICE_REVENUE", "Service / labour income"], ["FI_INCOME", "Finance and insurance income"], ["OTHER_REVENUE", "Other income"], ["PASS_THROUGH", "Collected for a third party"]];
const CHECKLIST_TYPES = [["DELIVERY", "Delivery"], ["PDI", "Pre-delivery inspection"], ["TEST_DRIVE", "Test drive"], ["APPRAISAL", "Trade-in appraisal"], ["FEEDBACK", "Feedback"], ["QC", "Workshop quality control"], ["RO_CHECKIN", "Workshop check-in"]];

function useTaxOptions() {
  const fin = useFinOptions(["tax-rates"]);
  return (fin["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code }));
}

/* ------------------------------ catalogue ------------------------------ */

function VariantInfo({ variant, onHide }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.listMaster("item-info-fields", { status: "ACTIVE" }), []);
  const [values, setValues] = useState(variant.additional_info || {});
  const fields = data || [];
  const save = async () => (await act(() => dmsService.saveVariantInfo(variant.id, values))) && onHide(true);
  return (
    <SlidePanel
      show
      onHide={() => onHide(false)}
      title={`Additional info – ${variant.name}`}
      width={520}
      footer={
        <>
          <SecondaryButton onClick={() => onHide(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={save}>Save</PrimaryButton>
        </>
      }
    >
      <LoadState loading={loading} error={error} onRetry={reload}>
        {fields.length === 0 ? (
          <EmptyState title="No additional info fields" text="Add them under Vehicle catalogue → Additional info fields." />
        ) : (
          fields.map((f) => (
            <Field key={f.code} label={f.name} htmlFor={`vi-${f.code}`}>
              {f.field_type === "YESNO" ? (
                <select id={`vi-${f.code}`} className="form-select" value={values[f.code] === true ? "YES" : values[f.code] === false ? "NO" : ""} onChange={(e) => setValues({ ...values, [f.code]: e.target.value === "" ? null : e.target.value === "YES" })}>
                  <option value="">—</option>
                  <option value="YES">Yes</option>
                  <option value="NO">No</option>
                </select>
              ) : (
                <input id={`vi-${f.code}`} type={f.field_type === "NUMBER" ? "number" : f.field_type === "DATE" ? "date" : "text"} className="form-control" style={inputStyle} value={values[f.code] ?? ""} onChange={(e) => setValues({ ...values, [f.code]: e.target.value })} />
              )}
            </Field>
          ))
        )}
      </LoadState>
    </SlidePanel>
  );
}

export function CatalogueSetup() {
  const [section, setSection] = useState("makes");
  const [changed, setChanged] = useState(0);
  const opts = useDmsOptions(["makes", "models"], changed);
  const tax = useTaxOptions();
  const [info, setInfo] = useState(null);
  const bump = () => setChanged((n) => n + 1);
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "makes", label: "Makes" },
          { key: "models", label: "Models" },
          { key: "variants", label: "Variants" },
          { key: "colours", label: "Colours" },
          { key: "info", label: "Additional info fields" },
        ]}
      />
      {section === "makes" ? (
        <MasterSection
          {...m("makes")}
          noun="make"
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Warranty claims to", (r) => r.warranty_account_number || "—"]]}
          fieldKeys={["name", "warranty_account_number"]}
          lockedOnEdit={["code"]}
          fields={() => [{ ...CODE, max: 16 }, NAME, { key: "warranty_account_number", label: "Warranty claims billed to (customer account number)", max: 30, hint: "The manufacturer's account in Receivables" }]}
        />
      ) : null}
      {section === "models" ? (
        <MasterSection
          {...m("models")}
          noun="model"
          deps={[changed]}
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Make", (r) => r.make_name], ["Body", (r) => labelOf(BODY, r.body_type)]]}
          fieldKeys={["make_id", "name", "body_type"]}
          lockedOnEdit={["code"]}
          toForm={(r) => ({ ...r, make_id: optionByCode(opts.makes, r.make_code) })}
          fields={() => [{ key: "make_id", label: "Make", type: "select", required: true, options: opts.makes || [] }, CODE, NAME, { key: "body_type", label: "Body type", type: "select", options: opt(BODY) }]}
          emptyText="Add the makes first."
        />
      ) : null}
      {section === "variants" ? (
        <MasterSection
          {...m("variants")}
          noun="variant"
          deps={[changed]}
          columns={[["Item code", (r) => r.code], ["Name", (r) => r.name], ["Model", (r) => r.model_name], ["Year", (r) => r.model_year], ["Fuel", (r) => labelOf(FUEL, r.fuel_type)], ["VAT", (r) => r.tax_rate_code]]}
          fieldKeys={["model_id", "name", "model_year", "trim", "fuel_type", "transmission", "engine", "seats", "warranty_months", "warranty_km", "tax_rate_code"]}
          lockedOnEdit={["code"]}
          defaults={{ model_year: new Date().getFullYear(), tax_rate_code: "AE_STD" }}
          toForm={(r) => ({ ...r, model_id: optionByCode(opts.models, r.model_code) })}
          extraActions={(r, done) => (
            <>
              <button type="button" style={linkBtn} onClick={() => setInfo({ row: r, done })}>
                Additional info
              </button>{" "}
            </>
          )}
          fields={() => [
            { key: "model_id", label: "Model", type: "select", required: true, options: opts.models || [] },
            { ...CODE, label: "Item code", max: 40 },
            { ...NAME, max: 150 },
            { key: "model_year", label: "Model year", type: "number", min: 1950, required: true },
            { key: "trim", label: "Trim", max: 60 },
            { key: "fuel_type", label: "Fuel", type: "select", options: opt(FUEL) },
            { key: "transmission", label: "Transmission", type: "select", options: opt(GEAR) },
            { key: "engine", label: "Engine", max: 40 },
            { key: "seats", label: "Seats", type: "number", min: 1 },
            { key: "warranty_months", label: "Warranty (months)", type: "number", min: 0 },
            { key: "warranty_km", label: "Warranty (km)", type: "number", min: 0 },
            { key: "tax_rate_code", label: "VAT code", type: "select", options: tax },
          ]}
          emptyText="Add the models first."
        />
      ) : null}
      {section === "colours" ? (
        <MasterSection
          {...m("colours")}
          noun="colour"
          columns={[["Type", (r) => (r.colour_type === "EXTERIOR" ? "Exterior" : "Interior")], ["Code", (r) => r.code], ["Name", (r) => r.name]]}
          fieldKeys={["name"]}
          lockedOnEdit={["code", "colour_type"]}
          defaults={{ colour_type: "EXTERIOR" }}
          fields={() => [{ key: "colour_type", label: "Exterior / interior", type: "select", required: true, options: [{ label: "Exterior", value: "EXTERIOR" }, { label: "Interior", value: "INTERIOR" }] }, CODE, NAME]}
        />
      ) : null}
      {section === "info" ? (
        <MasterSection
          {...m("item-info-fields")}
          noun="field"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => r.field_type], ["Order", (r) => r.sort_order]]}
          fieldKeys={["name", "field_type", "sort_order"]}
          lockedOnEdit={["code"]}
          defaults={{ field_type: "TEXT" }}
          fields={() => [
            { ...CODE, max: 32 },
            NAME,
            { key: "field_type", label: "Type", type: "select", required: true, options: opt([["TEXT", "Text"], ["NUMBER", "Number"], ["DATE", "Date"], ["YESNO", "Yes / no"]]) },
            { key: "sort_order", label: "Order", type: "number", min: 0 },
          ]}
        />
      ) : null}
      {info ? (
        <VariantInfo
          variant={info.row}
          onHide={(saved) => {
            if (saved) info.done();
            setInfo(null);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------ order types ------------------------------ */

export function OrderTypesSetup() {
  const [section, setSection] = useState("defs");
  const [changed, setChanged] = useState(0);
  const opts = useDmsOptions(["order-types", "price-lists"], changed);
  const fin = useFinOptions(["business-units"]);
  const trx = useLoad(() => finService.listTrxTypes(), []);
  const trxOptions = (trx.data || []).filter((t) => t.trx_class === "INV").map((t) => ({ label: `${t.code} – ${t.name}`, value: t.code }));
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "defs", label: "Order type definition" },
          { key: "setup", label: "Order type setup" },
        ]}
      />
      {section === "defs" ? (
        <MasterSection
          {...m("order-types")}
          noun="order type"
          onChanged={() => setChanged((n) => n + 1)}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Stock", (r) => labelOf(STOCK, r.stock_type)], ["Invoice type", (r) => r.ar_trx_type_code], ["Registration", (r) => (r.requires_registration ? "Required" : "—")], ["Checklist", (r) => (r.requires_checklist ? "Required" : "—")]]}
          fieldKeys={["name", "category", "stock_type", "ar_trx_type_code", "requires_registration", "requires_checklist"]}
          lockedOnEdit={["code"]}
          defaults={{ category: "VEHICLE", ar_trx_type_code: "VEHICLE_SALE", requires_registration: true, requires_checklist: true }}
          toForm={(r) => ({ ...r, requires_registration: !!r.requires_registration, requires_checklist: !!r.requires_checklist })}
          fields={() => [
            CODE,
            NAME,
            { key: "category", label: "Category", type: "select", required: true, options: opt([["VEHICLE", "Vehicles"], ["PARTS", "Parts"]]) },
            { key: "stock_type", label: "Stock type (blank for any)", type: "select", options: opt(STOCK) },
            { key: "ar_trx_type_code", label: "Invoice transaction type (Finance)", type: "select", required: true, options: trxOptions },
            { key: "requires_registration", label: "Registration must be entered before delivery", type: "checkbox" },
            { key: "requires_checklist", label: "The delivery checklist must be completed", type: "checkbox" },
          ]}
        />
      ) : null}
      {section === "setup" ? (
        <MasterSection
          {...m("order-type-setups")}
          noun="setup"
          deps={[changed]}
          columns={[["Business unit", (r) => r.business_unit_name], ["Customer type", (r) => (r.customer_type === "WALKIN" ? "Walk-in" : "Regular")], ["Order type", (r) => r.order_type_name], ["Price list", (r) => r.price_list_name]]}
          fieldKeys={["order_type_id", "price_list_id"]}
          lockedOnEdit={["business_unit_id", "customer_type", "category"]}
          defaults={{ customer_type: "REGULAR", category: "VEHICLE" }}
          toForm={(r) => ({ ...r, business_unit_id: optionByCode(fin["business-units"], r.business_unit_code), order_type_id: optionByCode(opts["order-types"], r.order_type_code), price_list_id: optionByCode(opts["price-lists"], r.price_list_code) })}
          fields={() => [
            { key: "business_unit_id", label: "Business unit", type: "select", required: true, options: fin["business-units"] || [] },
            { key: "customer_type", label: "Customer type", type: "select", required: true, options: opt([["REGULAR", "Regular"], ["WALKIN", "Walk-in"]]) },
            { key: "category", label: "Category", type: "select", required: true, options: opt([["VEHICLE", "Vehicles"], ["PARTS", "Parts"]]) },
            { key: "order_type_id", label: "Default order type", type: "select", required: true, options: opts["order-types"] || [] },
            { key: "price_list_id", label: "Default price list", type: "select", options: opts["price-lists"] || [] },
          ]}
          emptyText="Choose which order type and price list each business unit uses by default."
        />
      ) : null}
    </>
  );
}

/* ------------------------------ price lists ------------------------------ */

function PriceLines({ list, onHide }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.listPriceLines(list.id), [list.id]);
  const opts = useDmsOptions(["variants", "charge-types"]);
  const [adding, setAdding] = useState(false);
  return (
    <SlidePanel show onHide={onHide} title={`Prices – ${list.name} (${list.currency})`} width={820}>
      <div className="d-flex align-items-center gap-3 mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Add price</PrimaryButton>
        <span style={hint}>A new price from a date closes the previous one the day before.{list.prices_include_tax ? " Prices include VAT." : " Prices are before VAT."}</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          minWidth={620}
          empty={<EmptyState title="No prices yet" />}
          columns={[
            { label: "Item", render: (r) => `${r.item_code} – ${r.item_name}` },
            { label: "Type", render: (r) => (r.item_type === "VARIANT" ? "Vehicle" : "Service") },
            { label: "Price", num: true, render: (r) => fmtMoney(r.price) },
            { label: "Minimum", num: true, render: (r) => (r.min_price === null ? "—" : fmtMoney(r.min_price)) },
            { label: "From", render: (r) => formatDate(r.effective_from) },
            { label: "To", render: (r) => (r.effective_to ? formatDate(r.effective_to) : "—") },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={adding}
        title="Add price"
        initial={{ item_type: "VARIANT", effective_from: todayYmd() }}
        onHide={() => setAdding(false)}
        onDone={reload}
        onSave={(clean) => dmsService.savePriceLine(list.id, { ...clean, price: Number(clean.price), min_price: clean.min_price === undefined ? null : Number(clean.min_price) })}
        fields={(v) => [
          { key: "item_type", label: "Item type", type: "select", required: true, options: opt([["VARIANT", "Vehicle variant"], ["CHARGE", "Service offered"]]) },
          { key: "item_id", label: v.item_type === "CHARGE" ? "Service" : "Variant", type: "select", required: true, options: (v.item_type === "CHARGE" ? opts["charge-types"] : opts.variants) || [] },
          { key: "price", label: "Price", type: "number", step: "any", min: 0, required: true },
          { key: "min_price", label: "Minimum price (below it a manager approves)", type: "number", step: "any", min: 0 },
          { key: "effective_from", label: "From", type: "date", required: true },
          { key: "effective_to", label: "To", type: "date" },
        ]}
      />
    </SlidePanel>
  );
}

export function PriceListsSetup() {
  const fin = useFinOptions(["currencies"]);
  const [open, setOpen] = useState(null);
  return (
    <>
      <MasterSection
        {...m("price-lists")}
        noun="price list"
        columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Currency", (r) => r.currency], ["VAT", (r) => (r.prices_include_tax ? "Included" : "Added")]]}
        fieldKeys={["name", "prices_include_tax"]}
        lockedOnEdit={["code", "currency"]}
        defaults={{ currency: "AED" }}
        toForm={(r) => ({ ...r, prices_include_tax: !!r.prices_include_tax })}
        extraActions={(r) => (
          <>
            <button type="button" style={linkBtn} onClick={() => setOpen(r)}>
              Prices
            </button>{" "}
          </>
        )}
        fields={() => [CODE, NAME, { key: "currency", label: "Currency", type: "select", required: true, options: (fin.currencies || []).map((o) => ({ label: o.label, value: o.code })) }, { key: "prices_include_tax", label: "Prices include VAT", type: "checkbox" }]}
      />
      {open ? <PriceLines list={open} onHide={() => setOpen(null)} /> : null}
    </>
  );
}

/* ------------------------------ services offered ------------------------------ */

export function ServicesSetup() {
  const tax = useTaxOptions();
  return (
    <>
      <div style={{ ...hint, marginBottom: 12 }}>What can be added to a vehicle sale: registration, insurance, extended warranty, accessories, packages. Pass-through charges are collected for a third party (e.g. traffic fees) and are not income.</div>
      <MasterSection
        {...m("charge-types")}
        noun="service"
        columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Group", (r) => labelOf(GROUPS, r.charge_group)], ["Treatment", (r) => (r.treatment === "PASS_THROUGH" ? "Pass-through" : "Income")], ["Default price", (r) => fmtMoney(r.default_price)], ["VAT", (r) => r.tax_rate_code || "—"]]}
        fieldKeys={["name", "charge_group", "treatment", "line_type", "default_price", "tax_rate_code"]}
        lockedOnEdit={["code"]}
        defaults={{ charge_group: "SERVICE", treatment: "REVENUE", line_type: "CHARGE_REVENUE", tax_rate_code: "AE_STD" }}
        toForm={(r) => ({ ...r, default_price: Number(r.default_price) })}
        fields={(v) => [
          CODE,
          NAME,
          { key: "charge_group", label: "Group", type: "select", required: true, options: opt(GROUPS) },
          { key: "treatment", label: "Treatment", type: "select", required: true, options: opt([["REVENUE", "Our income"], ["PASS_THROUGH", "Collected for a third party"]]) },
          { key: "line_type", label: "Posts to", type: "select", required: true, options: opt(v.treatment === "PASS_THROUGH" ? LINE_TYPES.filter(([x]) => x === "PASS_THROUGH") : LINE_TYPES.filter(([x]) => x !== "PASS_THROUGH")) },
          { key: "default_price", label: "Default price", type: "number", step: "any", min: 0 },
          { key: "tax_rate_code", label: "VAT code", type: "select", options: tax },
        ]}
      />
    </>
  );
}

/* ------------------------------ campaigns ------------------------------ */

export function CampaignsSetup() {
  const opts = useDmsOptions(["makes", "models", "variants"]);
  const fin = useFinOptions(["projects"]);
  const scopeOptions = (t) => (t === "MAKE" ? opts.makes : t === "MODEL" ? opts.models : opts.variants) || [];
  return (
    <MasterSection
      {...m("campaigns")}
      noun="campaign"
      columns={[
        ["Code", (r) => r.code],
        ["Name", (r) => r.name],
        ["Applies to", (r) => `${r.scope_type.toLowerCase()} ${r.scope_name || ""}`],
        ["Discount", (r) => (r.discount_type === "PERCENT" ? `${Number(r.discount_value)}%` : fmtMoney(r.discount_value))],
        ["Maker support", (r) => (Number(r.oem_support_amount) ? fmtMoney(r.oem_support_amount) : "—")],
        ["Dates", (r) => `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`],
      ]}
      fieldKeys={["name", "scope_type", "scope_id", "stock_type", "discount_type", "discount_value", "oem_support_amount", "start_date", "end_date", "project_code"]}
      lockedOnEdit={["code"]}
      defaults={{ scope_type: "MODEL", discount_type: "AMOUNT", start_date: todayYmd() }}
      toForm={(r) => ({ ...r, scope_id: optionByCode(scopeOptions(r.scope_type), r.scope_code), discount_value: Number(r.discount_value), oem_support_amount: Number(r.oem_support_amount) })}
      fields={(v) => [
        CODE,
        { ...NAME, max: 150 },
        { key: "scope_type", label: "Applies to", type: "select", required: true, options: opt([["MAKE", "A make"], ["MODEL", "A model"], ["VARIANT", "A variant"]]) },
        { key: "scope_id", label: v.scope_type === "MAKE" ? "Make" : v.scope_type === "VARIANT" ? "Variant" : "Model", type: "select", required: true, options: scopeOptions(v.scope_type) },
        { key: "stock_type", label: "Stock type (blank for any)", type: "select", options: opt(STOCK) },
        { key: "discount_type", label: "Discount", type: "select", required: true, options: opt([["AMOUNT", "Amount"], ["PERCENT", "Percent of list price"]]) },
        { key: "discount_value", label: v.discount_type === "PERCENT" ? "Percent" : "Amount", type: "number", step: "any", min: 0, required: true },
        { key: "oem_support_amount", label: "Manufacturer support per vehicle (claimed on invoice)", type: "number", step: "any", min: 0 },
        { key: "start_date", label: "From", type: "date", required: true },
        { key: "end_date", label: "To", type: "date", required: true },
        { key: "project_code", label: "Finance project", type: "select", options: (fin.projects || []).map((o) => ({ label: o.label, value: o.code })) },
      ]}
    />
  );
}

/* ------------------------------ checklists ------------------------------ */

function ChecklistQuestions({ template, onHide }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.listChecklistItems(template.id), [template.id]);
  const [items, setItems] = useState(null);
  const rows = items || (data || []).filter((i) => i.status === "ACTIVE").map((i) => ({ question: i.question, answer_type: i.answer_type, is_mandatory: !!i.is_mandatory }));
  const set = (i, patch) => setItems(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const res = await dmsService.saveChecklistItems(template.id, rows).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success("Checklist saved");
      onHide();
    } else toast.error(responseMessage(res));
  };
  return (
    <SlidePanel
      show
      onHide={onHide}
      title={`Questions – ${template.name}`}
      width={720}
      footer={
        <>
          <SecondaryButton onClick={onHide}>Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={!rows.length}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.map((r, i) => (
          <div key={i} className="d-flex gap-2 align-items-center mb-2">
            <span style={{ width: 24, ...hint }}>{i + 1}</span>
            <input className="form-control" style={inputStyle} aria-label={`Question ${i + 1}`} value={r.question} onChange={(e) => set(i, { question: e.target.value })} />
            <select className="form-select" style={{ width: 120 }} aria-label={`Answer type ${i + 1}`} value={r.answer_type} onChange={(e) => set(i, { answer_type: e.target.value })}>
              <option value="YESNO">Yes / no</option>
              <option value="TEXT">Text</option>
              <option value="NUMBER">Number</option>
            </select>
            <label className="d-flex align-items-center gap-1" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={r.is_mandatory} onChange={(e) => set(i, { is_mandatory: e.target.checked })} /> Required
            </label>
            <button type="button" style={linkBtn} onClick={() => setItems(rows.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setItems([...rows, { question: "", answer_type: "YESNO", is_mandatory: true }])}>
          Add question
        </button>
      </LoadState>
    </SlidePanel>
  );
}

export function ChecklistsSetup() {
  const [open, setOpen] = useState(null);
  return (
    <>
      <MasterSection
        {...m("checklist-templates")}
        noun="checklist"
        columns={[["Used for", (r) => labelOf(CHECKLIST_TYPES, r.checklist_type)], ["Code", (r) => r.code], ["Name", (r) => r.name]]}
        fieldKeys={["name"]}
        lockedOnEdit={["code", "checklist_type"]}
        defaults={{ checklist_type: "DELIVERY" }}
        extraActions={(r) => (
          <>
            <button type="button" style={linkBtn} onClick={() => setOpen(r)}>
              Questions
            </button>{" "}
          </>
        )}
        fields={() => [{ key: "checklist_type", label: "Used for", type: "select", required: true, options: opt(CHECKLIST_TYPES) }, CODE, NAME]}
        emptyText="The first active checklist of each type is used on new documents."
      />
      {open ? <ChecklistQuestions template={open} onHide={() => setOpen(null)} /> : null}
    </>
  );
}

/* ------------------------------ sales team ------------------------------ */

export function SalesTeamSetup() {
  const fin = useFinOptions(["locations"]);
  return (
    <>
      <div style={{ ...hint, marginBottom: 12 }}>Sales consultants are HR employees. Give them the Sales Consultant permission under Access so they can work in the Dealership.</div>
      <MasterSection
        {...m("salespersons")}
        noun="sales consultant"
        columns={[["Name", (r) => r.employee_name], ["Location", (r) => r.location_name], ["Team", (r) => r.team]]}
        fieldKeys={["location_id", "team"]}
        lockedOnEdit={["employee_id"]}
        toForm={(r) => ({ ...r, location_id: optionByCode(fin.locations, r.location_code) })}
        fields={() => [
          { key: "employee_id", label: "Employee", type: "employee", required: true },
          { key: "location_id", label: "Location", type: "select", options: fin.locations || [] },
          { key: "team", label: "Team", max: 60 },
        ]}
      />
    </>
  );
}
